import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Alert, App, Form, Radio, Tooltip, Typography } from 'antd';
import type { RadioChangeEvent } from 'antd';

import MaskedInput from 'components/MaskedInput';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { anticipationService } from 'v2/services/anticipation';
import type { RequestAnticipation } from 'v2/services/anticipation';
import * as Yup from 'yup';

import { Icon } from '@eduzz/ui-antd-theme';

import InstallmentCashbackAlert from '../InstallmentCashbackAlert';
import { ANTICIPATION_FRAUD_GROUP, type Props, type AnticipationOptions, type AnticipationByValue } from './constants';

const HELP_ANTICIPATION_URL =
  'https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-';

function AnticipationModal({
  anticipationData,
  isOpen,
  setClose,
  updateTransferAvailable,
  handleUpdateAvailableAmount,
  handleOpenSuggestionAnticipation,
  loadSuggestionAnticipation,
  hasSuggestionAnticipation,
  anticipationGroupInfo
}: Props) {
  const history = useHistory();
  const { message } = App.useApp();

  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingByValue, setIsLoadingByValue] = useState(false);

  const [valueToAnticipate, setValueToAnticipate] = useState(null);
  const [valueToAnticipateByValue, setValueToAnticipateByValue] = useState<null | AnticipationByValue>(null);
  const [anticipateOptionSelected, setAnticipateOptionSelected] = useState('');

  const [formIsValid, setFormIsValid] = useState(true);
  const [formMessageError, setFormMessageError] = useState(null);

  const [currentBalance] = useObservable(() => anticipationService.getCurrentBalance(), []);
  const [totalInvoices] = useObservable(() => anticipationService.getTotalInvoices(), []);

  useObservable(() => anticipationService.loadData(), []);

  const [requestAnticipation] = useCallbackObservable((anticipation: RequestAnticipation) => {
    setIsLoading(true);

    return anticipationService.requestAnticipation(anticipation).pipe(
      tap(
        () => {
          setIsLoading(false);
          setShowSuccess(true);
          updateTransferAvailable();
          handleUpdateAvailableAmount();
        },
        err => {
          message.error(typeof err === 'string' ? err : 'Não foi possível solicitar a antecipação.');
          setIsLoading(false);
        }
      )
    );
  }, []);

  const [getAvailableByValue] = useCallbackObservable((requestedValue: number) => {
    setIsLoadingByValue(true);
    return anticipationService.getAvailableByValue(requestedValue).pipe(
      tap(({ data }) => setValueToAnticipateByValue(data)),
      tap(() => setIsLoadingByValue(false))
    );
  }, []);

  const nothingToAnticipate = !currentBalance && !showSuccess;
  const valueRequestItsTotal = currentBalance === valueToAnticipate;
  const hasAnticipationOptions = !!valueToAnticipateByValue;
  const somethingIsLoading = isLoading || isLoadingByValue;
  const anticipationOptionNotSelected = !valueRequestItsTotal && hasAnticipationOptions && !anticipateOptionSelected;
  const valueRequestedItsExact = !!valueToAnticipateByValue?.request_value;
  const requestedorTotalInvoices = valueToAnticipateByValue?.request_value?.invoices || totalInvoices;

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';
  const currentFixedFee = anticipationData?.anticipation_fees?.fixed_manual_fee
    ? `${anticipationData?.anticipation_fees?.fixed_manual_fee}%`
    : '2.99%';
  const currentDynamicFee = anticipationData?.anticipation_fees?.dynamic_manual_fee ?? 0.1;

  const validateValue = async (value: string) => {
    const anticipateSchema = Yup.object().shape({
      valueToAnticipate: Yup.number()
        .required()
        .min(0.01, `Valor mínimo: ${numberToBRL(0.01)}`)
        .max(currentBalance, `Valor máximo: ${balanceFormatted}`)
    });

    try {
      await anticipateSchema.validate({ valueToAnticipate: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const submitRequest = () => {
    const anticipationDataRequest = {
      amount_requested: currentBalance,
      total_invoices: totalInvoices
    };

    if (anticipateOptionSelected) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Antecipação_Parcial',
        label: `Sugestão_${anticipateOptionSelected === 'low_value' ? 'Menor' : 'Maior'}_Valor`
      });

      anticipationDataRequest.amount_requested = (
        valueToAnticipateByValue[
          anticipateOptionSelected as keyof typeof valueToAnticipateByValue
        ] as AnticipationOptions
      ).value;
      anticipationDataRequest.total_invoices = (
        valueToAnticipateByValue[
          anticipateOptionSelected as keyof typeof valueToAnticipateByValue
        ] as AnticipationOptions
      ).invoices;
    }

    if (valueRequestedItsExact) {
      anticipationDataRequest.amount_requested = valueToAnticipateByValue.request_value.value;
      anticipationDataRequest.total_invoices = valueToAnticipateByValue.request_value.invoices;
    }

    loadSuggestionAnticipation();
    requestAnticipation(anticipationDataRequest);
  };

  const handleOpenTransferModal = () => {
    history.push('/producer/financial-statement?a=transfer');
  };

  const handleGoToTranfer = () => {
    setIsLoading(true);
    setTimeout(() => {
      updateTransferAvailable();
      handleUpdateAvailableAmount();
      setIsLoading(false);
      handleOpenTransferModal();
      setClose();
    }, 3000);
  };

  const getCopyNextStepButton = () => {
    if (showSuccess) return 'Realizar transferência';
    if (!valueRequestItsTotal && !hasAnticipationOptions) return 'Verificar';
    return 'Confirmar antecipação';
  };

  const handleCloseSuccessAnticipation = () => {
    setClose();

    if (hasSuggestionAnticipation) {
      handleOpenSuggestionAnticipation();
    }
  };

  const getFunctionNextStepButton = () => {
    if (!valueRequestItsTotal && !hasAnticipationOptions) return handleGetAvailableByValue();
    if (!showSuccess) return submitRequest();
    return handleGoToTranfer();
  };

  const shouldDisableNextStepButton = () => {
    return (
      nothingToAnticipate ||
      !formIsValid ||
      (anticipationOptionNotSelected && !valueRequestedItsExact) ||
      somethingIsLoading
    );
  };

  const handleInputChange = (value: string) => {
    setValueToAnticipateByValue(null);
    setAnticipateOptionSelected('');
    validateValue(value);
    setValueToAnticipate(value);
  };

  const handleChangeAnticipationOptions = (event: RadioChangeEvent) => setAnticipateOptionSelected(event.target.value);

  const handleGetAvailableByValue = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Antecipação_Parcial',
      label: 'Verificar'
    });

    getAvailableByValue(valueToAnticipate);
  };

  const getAnticipationOption = (props: AnticipationOptions, optionValue: string) => {
    if (!props) return null;

    return (
      <Radio
        value={optionValue}
        className='m-0 flex w-full rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container) p-3'
      >
        <div className='flex flex-col gap-1'>
          <Typography.Text strong>{numberToBRL(props.value)}</Typography.Text>
          <Typography.Text type='secondary' className='text-sm'>
            Tarifa: {numberToBRL(props.fee)}
          </Typography.Text>
          <Typography.Text type='secondary' className='text-sm'>
            Faturas: {props.invoices}
          </Typography.Text>
        </div>
      </Radio>
    );
  };

  useEffect(() => {
    if (currentBalance) {
      setValueToAnticipate(currentBalance);
    }
  }, [currentBalance]);

  return (
    <Modal
      open={isOpen}
      size='sm'
      setClose={setClose}
      textTitle={!showSuccess ? 'Antecipar saldo' : 'Parabéns!'}
      textReturnButton='Agora não'
      textProceedButton={getCopyNextStepButton()}
      handleProceedClick={getFunctionNextStepButton}
      handleReturnClick={!showSuccess ? setClose : handleCloseSuccessAnticipation}
      isProceedButtonDisabled={shouldDisableNextStepButton()}
      isProceedButtonLoading={somethingIsLoading}
    >
      {!showSuccess && (
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Typography.Text>Saldo disponível para antecipação:</Typography.Text>
            <Typography.Title level={4} className='m-0 text-(--uizz-antd-color-primary)'>
              {balanceFormatted}
            </Typography.Title>
          </div>

          <Form layout='vertical' className='flex flex-col gap-3'>
            <Form.Item
              label='Digite o valor desejado'
              validateStatus={!formIsValid ? 'error' : undefined}
              help={!formIsValid && formMessageError ? formMessageError : undefined}
              className='mb-0'
            >
              <MaskedInput
                className='w-full'
                name='valueToAnticipate'
                placeHolder='R$ 0,00'
                mask='money'
                maskCleanValue
                fullWidth
                disabled={nothingToAnticipate || somethingIsLoading}
                value={valueToAnticipate}
                onChange={handleInputChange}
                error={!formIsValid}
                allowClear
              />
            </Form.Item>

            {(valueRequestItsTotal || valueRequestedItsExact) && (
              <Typography.Text type='secondary'>
                {`Esse valor é referente a ${requestedorTotalInvoices} fatura${
                  requestedorTotalInvoices > 1 ? 's' : ''
                } antecipáve${requestedorTotalInvoices > 1 ? 'is' : 'l'}.`}
              </Typography.Text>
            )}

            {!valueRequestItsTotal && !valueRequestedItsExact && valueToAnticipateByValue && (
              <div className='flex flex-col gap-3 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
                <div className='flex items-center gap-1'>
                  <Typography.Text>Escolha o melhor valor para suas necessidades:</Typography.Text>
                  <Tooltip
                    title='Os valores sugeridos foram obtidos das faturas antecipáveis (da venda mais antiga para a mais nova).'
                    placement='top'
                  >
                    <span className='inline-flex cursor-help'>
                      <Icon.CircleHelp size={16} className='text-(--uizz-antd-color-text-secondary)' />
                    </span>
                  </Tooltip>
                </div>

                <Radio.Group
                  value={anticipateOptionSelected}
                  onChange={handleChangeAnticipationOptions}
                  className='grid w-full grid-cols-1 gap-3 sm:grid-cols-2'
                >
                  {getAnticipationOption(valueToAnticipateByValue?.low_value, 'low_value')}
                  {getAnticipationOption(valueToAnticipateByValue?.up_value, 'up_value')}
                </Radio.Group>
              </div>
            )}

            {anticipationGroupInfo?.id === ANTICIPATION_FRAUD_GROUP && (
              <Alert
                showIcon
                type='warning'
                message={`Sua antecipação foi limitada para ${anticipationGroupInfo.percent}% do valor disponível para antecipação! Em caso de dúvidas entre em contato com o nosso suporte.`}
              />
            )}

            <InstallmentCashbackAlert enabled={isOpen} />

            <Typography.Text type='secondary'>
              O recebimento antes do prazo incorre tarifa administrativa de {currentFixedFee} + {currentDynamicFee}% por
              dia antecipado.{' '}
              <Typography.Link href={HELP_ANTICIPATION_URL} target='_blank' rel='noopener noreferrer'>
                Saiba mais
              </Typography.Link>
            </Typography.Text>
          </Form>
        </div>
      )}

      {showSuccess && (
        <div className='flex flex-col items-center gap-3 text-center'>
          <img width={64} height={63} src='/img/success_icon.svg' alt='Ícone verde de sucesso' />
          <Typography.Paragraph className='mb-0'>
            Parabéns, sua solicitação de antecipação de valores foi efetuada com sucesso!
          </Typography.Paragraph>
          <Typography.Text strong>
            A lista de faturas antecipadas será enviada para o seu e-mail assim que a solicitação for processada.
          </Typography.Text>
          <Typography.Text strong>Em alguns instantes seu saldo disponível estará atualizado.</Typography.Text>
        </div>
      )}
    </Modal>
  );
}

export default AnticipationModal;
